// ====================================================================
// File: src/articles/WaecPhysicsTips.jsx
// Article: Top 20 WAEC Physics Questions You Should Not Ignore
// (Mixed Theory + Calculations Version)
// ====================================================================

import { Typography, Divider } from "@mui/material";



export default function WaecPhysicsTips() {
  return (
    <>
      {/* INTRO */}
      <Typography sx={{ mb: 2 }}>
        Physics consistently produces some of the most difficult questions in
        WAEC. Many students fail not because the subject is too hard, but
        because they neglect the high-frequency topics WAEC repeats every year.
      </Typography>

      <Typography sx={{ mb: 3 }}>
        This guide presents 20 essential WAEC Physics questions — a balanced mix
        of calculations and theory — with easy-to-understand explanations to
        strengthen your preparation.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* ============== QUESTION 1 ============== */}
      <Typography variant="h5" fontWeight={700} sx={{ mt: 3 }}>
        1. A car starts from rest and accelerates uniformly at 2 m/s² for 10
        seconds. Calculate its final velocity.
      </Typography>

      <Typography sx={{ mt: 1.5 }}>
        Using the first equation of motion:
      </Typography>

      <Typography sx={{ mt: 1 }}>
        v = u + at  
        = 0 + (2 × 10)  
        = <strong>20 m/s</strong>
      </Typography>

      <Typography sx={{ mt: 1, mb: 3 }}>
        <strong>Concept Tested:</strong> Linear acceleration.
      </Typography>

      {/* ============== QUESTION 2 ============== */}
      <Typography variant="h5" fontWeight={700}>
        2. A force of 15 N stretches a spring by 5 cm. What is the spring
        constant?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Using Hooke’s law: <strong>F = kx</strong>  
        k = F/x = 15 / 0.05 = <strong>300 N/m</strong>
      </Typography>

      <Typography sx={{ mt: 1, mb: 3 }}>
        <strong>Concept Tested:</strong> Elasticity.
      </Typography>

      {/* ============== QUESTION 3 ============== */}
      <Typography variant="h5" fontWeight={700}>
        3. A 12 Ω resistor is connected to a 6 V battery. Calculate the current.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Using Ohm’s law: I = V/R = 6/12 = <strong>0.5 A</strong>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        <strong>Concept Tested:</strong> Basic electricity.
      </Typography>

      {/* ============== QUESTION 4 ============== */}
      <Typography variant="h5" fontWeight={700}>
        4. Which colour of light has the shortest wavelength?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        <strong>Violet</strong> light.
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* ============== QUESTION 5 ============== */}
      <Typography variant="h5" fontWeight={700}>
        5. A body of mass 4 kg is lifted to a height of 3 m. Calculate its
        potential energy.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        PE = mgh = 4 × 10 × 3 = <strong>120 J</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* ============== QUESTION 6 ============== */}
      <Typography variant="h5" fontWeight={700}>
        6. A ray of light strikes a plane mirror at 35°. What is the angle of
        reflection?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Angle of reflection = <strong>35°</strong>.
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* ============== QUESTION 7 ============== */}
      <Typography variant="h5" fontWeight={700}>
        7. A transformer steps 240 V down to 12 V and has 600 turns on the
        primary coil. How many turns are on the secondary coil?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Vs/Vp = Ns/Np  
        12/240 = Ns/600  
        Ns = 30 turns  
        <strong>Answer: 30 turns</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* ============== QUESTION 8 ============== */}
      <Typography variant="h5" fontWeight={700}>
        8. Which type of lens corrects short-sightedness?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        <strong>Concave (diverging) lens.</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* ============== QUESTION 9 ============== */}
      <Typography variant="h5" fontWeight={700}>
        9. A machine has an efficiency of 80%. If 200 J of work input is
        supplied, calculate the useful output.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Output = 0.8 × 200 = <strong>160 J</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* ============== QUESTION 10 ============== */}
      <Typography variant="h5" fontWeight={700}>
        10. Calculate the pressure exerted by a force of 40 N on an area of 5
        m².
      </Typography>

      <Typography sx={{ mt: 1 }}>
        P = F/A = 40/5 = <strong>8 N/m²</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 11 */}
      <Typography variant="h5" fontWeight={700}>
        11. What happens to the boiling point of water when pressure increases?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Boiling point **increases** with pressure.
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 12 */}
      <Typography variant="h5" fontWeight={700}>
        12. A current of 3 A flows for 10 seconds. Calculate the charge.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Q = It = 3 × 10 = <strong>30 C</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 13 */}
      <Typography variant="h5" fontWeight={700}>
        13. Which wave property determines loudness?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        <strong>Amplitude.</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 14 */}
      <Typography variant="h5" fontWeight={700}>
        14. An object 10 cm from a concave mirror forms an image 20 cm in front.
        Calculate the focal length.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Using mirror formula:  
        1/f = 1/v + 1/u  
        u = –10 cm, v = +20 cm  
        1/f = 1/20 – 1/10 = –1/20  
        f = <strong>–20 cm</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 15 */}
      <Typography variant="h5" fontWeight={700}>
        15. A heater rated 1000 W runs for 5 minutes. Calculate the energy used.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        E = Pt = 1000 × 300 = <strong>300,000 J</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 16 */}
      <Typography variant="h5" fontWeight={700}>
        16. What charge carriers move in metallic conduction?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        <strong>Electrons</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 17 */}
      <Typography variant="h5" fontWeight={700}>
        17. A wave has frequency 50 Hz and wavelength 2 m. Find its speed.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        v = fλ = 50 × 2 = <strong>100 m/s</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 18 */}
      <Typography variant="h5" fontWeight={700}>
        18. What device converts AC to DC?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        A **rectifier**
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 19 */}
      <Typography variant="h5" fontWeight={700}>
        19. A 2 kg object is heated from 20°C to 70°C. c = 4200 J/kg°C.  
        Find the heat supplied.
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Q = mcΔT = 2 × 4200 × 50 = <strong>420,000 J</strong>
      </Typography>

      <Typography sx={{ mb: 3 }} />

      {/* QUESTION 20 */}
      <Typography variant="h5" fontWeight={700}>
        20. Which electromagnetic wave is used in remote controls?
      </Typography>

      <Typography sx={{ mt: 1 }}>
        **Infrared radiation**
      </Typography>

      <Divider sx={{ my: 4 }} />

      <Typography>
        Master these questions and practice consistently — it will greatly
        improve your WAEC Physics performance.
      </Typography>
	  
	   
	  
    </>
  );
}
